import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getLatestPrices, getAlerts, seedDemoData, getLatestAnalysis, checkAlerts, sendWeeklySummary, getRecommendations } from '../services/api';
import { sortContracts, deduplicateContracts } from '../utils/contractUtils';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Users, Bell, TrendingUp, Phone, Zap, Flame, AlertTriangle, Activity, RefreshCw, Mail, Target, SendHorizonal } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PriceChart from '../components/PriceChart';
import AlertCard from '../components/AlertCard';
import RecommendationCard from '../components/RecommendationCard';

const StatCard = ({ icon: Icon, label, value, color = 'blue', trend }) => {
  const colorClasses = {
    blue: 'bg-[#397DFF]/10 text-[#397DFF]',
    green: 'bg-[#10D183]/10 text-[#10D183]',
    yellow: 'bg-[#F9C80E]/10 text-[#F9C80E]',
    red: 'bg-red-50 text-red-500'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-4 md:p-5 hover:shadow-md transition-shadow duration-200"
    >
      <div className="flex items-center justify-between mb-2 md:mb-3">
        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4 md:w-5 md:h-5" />
        </div>
        {trend && (
          <span className={`text-xs md:text-sm font-medium ${trend >= 0 ? 'text-[#10D183]' : 'text-red-500'}`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-2xl md:text-3xl font-bold text-[#191D1E]">{value}</p>
      <p className="text-xs md:text-sm text-[#687175] mt-1">{label}</p>
    </motion.div>
  );
};

const PriceIndicator = ({ label, value, change, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-[#397DFF]/10 text-[#397DFF]',
    yellow: 'bg-[#F9C80E]/10 text-[#F9C80E]'
  };
  
  return (
    <div className="flex items-center justify-between p-3 bg-[#F5F6F9] rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-sm text-[#687175]">{label}</p>
          <p className="font-mono font-bold text-[#191D1E]">{value} €/MWh</p>
        </div>
      </div>
      {change !== undefined && (
        <span className={`text-sm font-medium ${change >= 0 ? 'text-red-500' : 'text-[#10D183]'}`}>
          {change >= 0 ? '+' : ''}{change?.toFixed(2)}%
        </span>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [prices, setPrices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [checking, setChecking] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, pricesRes, alertsRes, analysisRes, recsRes] = await Promise.all([
        getDashboardStats(),
        getLatestPrices(),
        getAlerts(),
        getLatestAnalysis(),
        getRecommendations()
      ]);
      setStats(statsRes.data);
      setPrices(pricesRes.data);
      setAlerts(alertsRes.data);
      setAnalysis(analysisRes.data);
      setRecommendations(recsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedDemoData();
      toast.success('Données de démonstration chargées !');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setSeeding(false);
    }
  };

  const handleCheckAlerts = async () => {
    setChecking(true);
    try {
      const response = await checkAlerts();
      const data = response.data;
      if (data.triggered > 0) {
        toast.success(`${data.triggered} nouveau(x) seuil(s) franchi(s) — consultez "Alertes à envoyer"`);
      } else {
        toast.info('Aucun nouveau seuil franchi');
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la vérification des alertes');
    } finally {
      setChecking(false);
    }
  };

  const handleSendSummary = async () => {
    setSendingSummary(true);
    try {
      const response = await sendWeeklySummary();
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du récapitulatif');
    } finally {
      setSendingSummary(false);
    }
  };

  // Deduplicate contracts: keep most recent entry per base contract name
  // Group prices by energy type
  const electricityPrices = sortContracts(deduplicateContracts(prices.filter(p => p.energy_type === 'electricity')));
  const gasPrices = sortContracts(deduplicateContracts(prices.filter(p => p.energy_type === 'gas')));
  const spotElec = electricityPrices.find(p => p.contract_type === 'spot');
  const spotGas = gasPrices.find(p => p.contract_type === 'spot');

  // Get triggered alerts
  const triggeredAlerts = alerts.filter(a => a.triggered_at !== null);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="admin-dashboard-loading">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 bg-white" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80 bg-white" />
          <Skeleton className="h-80 bg-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#191D1E]">
            Bonjour !
          </h1>
          <p className="text-[#687175] mt-1">Voici un aperçu de votre activité</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleSendSummary}
            disabled={sendingSummary}
            className="btn-secondary"
            data-testid="send-summary-btn"
          >
            <Mail className={`w-4 h-4 mr-2`} />
            {sendingSummary ? 'Envoi...' : 'Envoyer récap'}
          </Button>
          <Button
            onClick={handleCheckAlerts}
            disabled={checking}
            className="btn-primary"
            data-testid="check-alerts-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Vérification...' : 'Vérifier alertes'}
          </Button>
          {prices.length === 0 && (
            <Button 
              onClick={handleSeedData} 
              disabled={seeding}
              className="btn-secondary"
              data-testid="seed-data-btn"
            >
              {seeding ? 'Chargement...' : 'Charger données démo'}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard icon={Users} label="Clients" value={stats?.clients_count || 0} color="blue" />
        <StatCard icon={Bell} label="Alertes actives" value={stats?.active_alerts || 0} color="green" />
        <div 
          onClick={() => navigate('/admin/pending-alerts')} 
          className="cursor-pointer"
          data-testid="pending-alerts-card"
        >
          <StatCard icon={SendHorizonal} label="Alertes à envoyer" value={stats?.pending_alerts_to_send || 0} color={stats?.pending_alerts_to_send > 0 ? 'red' : 'yellow'} />
        </div>
        <StatCard icon={Phone} label="Rappels en attente" value={stats?.pending_callbacks || 0} color="red" />
      </div>

      {/* Current Market Prices */}
      <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 md:p-6 hover:shadow-md transition-shadow duration-200">
        <h2 className="text-lg font-bold text-[#191D1E] mb-4">Prix du marché actuels</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-[#F9C80E] flex items-center gap-2">
              <Zap className="w-4 h-4" /> Électricité
            </h3>
            {spotElec ? (
              <PriceIndicator 
                label={spotElec.contract_name}
                value={spotElec.price?.toFixed(2)}
                change={spotElec.variation_pct}
                icon={Zap}
                color="yellow"
              />
            ) : (
              <p className="text-[#687175] text-sm">Aucun prix disponible</p>
            )}
            {electricityPrices.filter(p => p.contract_type === 'calendar').slice(0, 2).map(price => (
              <PriceIndicator 
                key={price.contract_name}
                label={price.contract_name}
                value={price.price?.toFixed(2)}
                change={price.variation_pct}
                icon={Zap}
                color="yellow"
              />
            ))}
          </div>
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-[#397DFF] flex items-center gap-2">
              <Flame className="w-4 h-4" /> Gaz Naturel
            </h3>
            {spotGas ? (
              <PriceIndicator 
                label={spotGas.contract_name}
                value={spotGas.price?.toFixed(2)}
                change={spotGas.variation_pct}
                icon={Flame}
                color="blue"
              />
            ) : (
              <p className="text-[#687175] text-sm">Aucun prix disponible</p>
            )}
            {gasPrices.filter(p => p.contract_type === 'calendar').slice(0, 2).map(price => (
              <PriceIndicator 
                key={price.contract_name}
                label={price.contract_name}
                value={price.price?.toFixed(2)}
                change={price.variation_pct}
                icon={Flame}
                color="blue"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Market Analysis */}
      {analysis?.analysis && (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 md:p-6 hover:shadow-md transition-shadow duration-200">
          <h2 className="text-lg font-bold text-[#191D1E] mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#397DFF]" /> Analyse du marché
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {analysis.analysis.electricity_summary && (
              <div className="p-4 bg-[#F9C80E]/5 rounded-lg border border-[#F9C80E]/20">
                <h4 className="text-sm font-medium text-[#F9C80E] mb-2">Électricité</h4>
                <p className="text-sm text-[#687175]">{analysis.analysis.electricity_summary}</p>
              </div>
            )}
            {analysis.analysis.gas_summary && (
              <div className="p-4 bg-[#397DFF]/5 rounded-lg border border-[#397DFF]/20">
                <h4 className="text-sm font-medium text-[#397DFF] mb-2">Gaz Naturel</h4>
                <p className="text-sm text-[#687175]">{analysis.analysis.gas_summary}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-white border border-[#BFC6CC]/12 rounded-2xl p-5 md:p-6 hover:shadow-md transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#191D1E] flex items-center gap-2">
              <Target className="w-5 h-5 text-[#397DFF]" /> Top Recommandations
            </h2>
            <a href="/admin/recommendations" className="text-sm text-[#397DFF] hover:underline font-medium" data-testid="view-all-recommendations">
              Voir tout
            </a>
          </div>
          <div className="space-y-3">
            {recommendations.filter(r => r.label === 'favorable').slice(0, 4).map((rec, i) => (
              <RecommendationCard key={rec.contract_name} rec={rec} index={i} compact />
            ))}
            {recommendations.filter(r => r.label === 'favorable').length === 0 && (
              <p className="text-sm text-[#687175] py-2">Aucune recommandation favorable pour le moment</p>
            )}
          </div>
        </div>
      )}

      {/* Triggered Alerts */}
      {triggeredAlerts.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-[#191D1E] mb-4">Alertes déclenchées récemment</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {triggeredAlerts.slice(0, 3).map(alert => (
              <AlertCard key={alert.id} alert={alert} showActions={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
